import { useState } from "react";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "./ui/dialog";
import { Input } from "./ui/input";
import { Button } from "./ui/button";
import { api, errMsg } from "../lib/api";
import { CompetitionSelect } from "./CompetitionSelect";
import { OptionPills } from "./OptionPills";
import { InviteCode } from "./InviteCode";
import { LimitsFields, limitsError, normalizeLimits } from "./LimitsFields";
import { RoundRangeFields } from "./RoundRangeFields";

export const SCORING = [
  ["fisse", "Punti fissa", "Punti se indovini l'esito secco della partita.", [1, 2, 3]],
  ["doppie", "Punti doppia", "Punti se l'esito rientra nella doppia scelta.", [0.5, 1, 2, 3]],
  ["esatto", "Punti risultato esatto", "Punti extra se indovini il risultato esatto.", [1, 2, 3, 4, 5]],
  ["bonus", "Bonus giornata", "Punti extra se azzecchi tutte le partite di una giornata.", [0, 5, 10, 15, 20, 25, 30]],
];
export const LIMITS = [["fisse", "Max fisse"], ["doppie", "Max doppie"], ["esatti", "Max esatti"]];
const INIT = { name: "", competition: "SA", scoring: { fisse: 2, doppie: 1, esatto: 3, bonus: 10 }, limits: { fisse: 5, doppie: 5, esatti: 1 }, start_round: null, end_round: null };

export const Section = ({ title, children }) => (
  <div className="space-y-3">
    <p className="font-mono text-[10px] uppercase tracking-widest text-slate-400">{title}</p>
    {children}
  </div>
);

export const CreateTournamentDialog = ({ open, onOpenChange, onCreated }) => {
  const [f, setF] = useState(INIT);
  const [created, setCreated] = useState(null);
  const [busy, setBusy] = useState(false);
  const nav = useNavigate();

  const submit = async (e) => {
    e.preventDefault();
    const err = limitsError(f.limits, f.competition);
    if (err) return toast.error(err);
    setBusy(true);
    try {
      const r = await api.post("/tournaments", f);
      setCreated(r.data);
      onCreated?.();
      toast.success("Torneo creato! Condividi il codice");
    } catch (err) {
      toast.error(errMsg(err));
    } finally {
      setBusy(false);
    }
  };

  const close = (o) => {
    onOpenChange(o);
    if (!o) setTimeout(() => { setCreated(null); setF(INIT); }, 200);
  };

  return (
    <Dialog open={open} onOpenChange={close}>
      <DialogContent className="bg-[#121824] border-[#26334D] max-w-lg max-h-[90vh] overflow-y-auto" data-testid="create-tournament-dialog">
        <DialogHeader>
          <DialogTitle className="font-display text-3xl font-black uppercase">{created ? "Torneo creato" : "Crea torneo"}</DialogTitle>
        </DialogHeader>
        {created ? (
          <div className="space-y-6">
            <p className="text-slate-300">Condividi questo codice con i tuoi amici per farli entrare in <b>{created.name}</b>.</p>
            <InviteCode code={created.invite_code} name={created.name} large />
            <Button onClick={() => nav(`/tournament/${created.id}`)} data-testid="go-to-created-tournament" className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold">
              Vai al torneo
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="space-y-6">
            <Section title="Nome torneo">
              <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required data-testid="create-name-input" className="h-11 bg-[#0A0D14] border-[#26334D]" placeholder="Es. Lega del Bar" />
            </Section>
            <Section title="Competizione">
              <CompetitionSelect value={f.competition} onChange={(v) => setF({ ...f, competition: v, limits: normalizeLimits({ ...f.limits, unlimited: false }, v) })} testId="create-competition-select" />
            </Section>
            <Section title="Durata del torneo">
              <RoundRangeFields competition={f.competition} start={f.start_round} end={f.end_round} testPrefix="create"
                fetchRounds={(c) => api.get(`/competitions/${c}/rounds`).then((r) => r.data)}
                onChange={(r) => setF((prev) => ({ ...prev, ...r }))} />
            </Section>
            {SCORING.map(([k, label, hint, opts]) => (
              <Section key={k} title={label}>
                <p className="text-xs text-slate-500 -mt-2">{hint}</p>
                <OptionPills options={opts} value={f.scoring[k]} onChange={(v) => setF({ ...f, scoring: { ...f.scoring, [k]: v } })} testId={`create-scoring-${k}`} />
              </Section>
            ))}
            <Section title="Limiti per giornata">
              <LimitsFields value={f.limits} competition={f.competition} onChange={(limits) => setF({ ...f, limits })} testPrefix="create" />
            </Section>
            <Button type="submit" disabled={busy} data-testid="create-tournament-submit" className="w-full h-11 rounded-full bg-emerald-500 hover:bg-emerald-400 text-[#0A0D14] font-bold uppercase tracking-wider">
              {busy ? "Creazione…" : "Crea torneo"}
            </Button>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
